/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { CertificateHelper } from '@app/app/shared/helpers/certificate.helper';
import { PrintPageHelper } from '@app/app/shared/helpers/print-page/print-page.helper';
import {
  Certificate,
  CertificateRequest,
  CertificateTemplate,
  PublicUserCertificateDetails,
  UserCertificateDetails,
} from '@app/app/shared/models/certificate/models/certificate.model';
import { ObservableResult } from '@app/app/shared/store';
import { Observable, defer } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { CertificateDataService } from './certificate-data.service';

const STORAGE_KEY = 'ptl_mock_certificates';

function classicPreviewSvg(): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="320" height="226" viewBox="0 0 320 226">
      <rect width="320" height="226" fill="#faf6ea" />
      <rect x="10" y="10" width="300" height="206" fill="none" stroke="#b48a3f" stroke-width="2" />
      <rect x="16" y="16" width="288" height="194" fill="none" stroke="#b48a3f" stroke-width="1" />
      <text x="160" y="88" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="12" letter-spacing="3" fill="#8a6a2f">CERTIFICATE</text>
      <text x="160" y="112" text-anchor="middle" font-family="Georgia, 'Times New Roman', serif" font-size="18" fill="#2c2416">of Completion</text>
      <line x1="120" y1="150" x2="200" y2="150" stroke="#b48a3f" stroke-width="1" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function modernPreviewSvg(): string {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="320" height="226" viewBox="0 0 320 226">
      <rect width="320" height="226" fill="#ffffff" />
      <rect width="320" height="48" fill="#1f2a44" />
      <circle cx="284" cy="24" r="14" fill="#3fd0c9" />
      <text x="20" y="88" font-family="'Helvetica Neue', Arial, sans-serif" font-size="10" letter-spacing="2" fill="#3fd0c9">CERTIFICATE OF COMPLETION</text>
      <text x="20" y="118" font-family="'Helvetica Neue', Arial, sans-serif" font-size="16" font-weight="700" fill="#1f2a44">Program title</text>
      <line x1="20" y1="180" x2="100" y2="180" stroke="#1f2a44" stroke-width="1.5" />
    </svg>`.trim();
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const SEEDED_TEMPLATES: CertificateTemplate[] = [
  {
    uid: 'template-classic-landscape',
    name: 'Classic · landscape',
    orientation: 'LANDSCAPE',
    previewImageUrl: classicPreviewSvg(),
    fieldSlots: [
      'LEARNER_NAME',
      'PROGRAM_TITLE',
      'SIGNATORY_NAME',
      'SIGNATORY_ROLE',
      'COMPLETION_DATE',
      'INSTITUTION_LOGO',
      'VERIFICATION_QR',
    ],
  },
  {
    uid: 'template-modern-landscape',
    name: 'Modern · landscape',
    orientation: 'LANDSCAPE',
    previewImageUrl: modernPreviewSvg(),
    fieldSlots: [
      'LEARNER_NAME',
      'PROGRAM_TITLE',
      'SIGNATORY_NAME',
      'SIGNATORY_ROLE',
      'COMPLETION_DATE',
      'INSTITUTION_LOGO',
      'VERIFICATION_QR',
    ],
  },
];

const SEEDED_ISSUED_CERTIFICATES: UserCertificateDetails[] = [
  {
    uid: 'issued-cert-001',
    certificateId: 'template-classic-landscape',
    resourceId: 'resource-001',
    title: 'Advanced Data Analytics Playlist',
    issuingOrganization: 'Potential.ly',
    completionDate: '2026-06-15',
    issuedAt: '2026-06-15T10:00:00',
    verification: { qrLinksTo: 'https://verify.yuna.dev/c/issued-cert-001' },
  },
  {
    uid: 'issued-cert-002',
    certificateId: 'template-modern-landscape',
    resourceId: 'resource-002',
    title: 'Leadership Fundamentals',
    issuingOrganization: 'Potential.ly',
    completionDate: '2025-02-10',
    issuedAt: '2025-02-10T09:30:00',
    expiresOn: '2027-02-10',
    verification: { qrLinksTo: 'https://verify.yuna.dev/c/issued-cert-002' },
  },
];

/**
 * Mock implementation only — the certificate backend is a separate parallel track (YUNA-1602).
 * State is kept in localStorage (falling back to an in-memory map) so a certificate survives
 * navigating away and back during a demo walkthrough.
 */
@Injectable()
export class MockCertificateDataService implements CertificateDataService {
  private readonly store: Record<string, Certificate> = this.loadStore();

  getTemplates(): ObservableResult<CertificateTemplate[]> {
    return ObservableResult.ofSuccess(SEEDED_TEMPLATES);
  }

  getCertificate(resourceId: string): ObservableResult<Certificate | undefined> {
    return ObservableResult.ofSuccess(this.store[resourceId]);
  }

  saveCertificate(resourceId: string, request: CertificateRequest): ObservableResult<Certificate> {
    const existing = this.store[resourceId];
    const certificate: Certificate = {
      uid: existing?.uid ?? uuidv4(),
      resourceId,
      title: request.title ?? existing?.title ?? '',
      status: existing?.status ?? 'INACTIVE',
      source: 'TEMPLATE',
      templateId: request.templateId ?? existing?.templateId,
      signatory: request.signatory ?? existing?.signatory ?? { name: '', role: '' },
      institutionLogoUrl: request.institutionLogoUrl ?? existing?.institutionLogoUrl,
      verification: existing?.verification ?? { qrLinksTo: `https://verify.yuna.dev/c/${uuidv4()}` },
      expiration: request.expiration ?? existing?.expiration ?? { type: 'NEVER' },
    };
    this.store[resourceId] = certificate;
    this.persistStore();
    return ObservableResult.ofSuccess(certificate);
  }

  updateCertificateActiveStatus(resourceId: string, status: boolean): ObservableResult<Certificate> {
    const existing = this.store[resourceId];
    if (!existing) {
      return ObservableResult.ofError('No certificate to activate yet');
    }
    existing.status = status ? 'ACTIVE' : 'INACTIVE';
    this.persistStore();
    return ObservableResult.ofSuccess(existing);
  }

  downloadSamplePdf(_resourceId: string, _request: CertificateRequest, previewElements?: NodeListOf<Element>): Observable<Blob> {
    return defer(async () => {
      const doc = await PrintPageHelper.addPages(previewElements ?? ([] as unknown as NodeListOf<Element>));
      return doc.output('blob');
    });
  }

  getIssuedCertificates(): ObservableResult<UserCertificateDetails[]> {
    return ObservableResult.ofSuccess(SEEDED_ISSUED_CERTIFICATES);
  }

  downloadIssuedPdf(_resourceId: string): Observable<Blob> {
    return defer(async () => {
      const doc = await PrintPageHelper.addPages([] as unknown as NodeListOf<Element>);
      return doc.output('blob');
    });
  }

  getCertificateVerification(userCertificateUid: string): ObservableResult<PublicUserCertificateDetails | undefined> {
    const issued = SEEDED_ISSUED_CERTIFICATES.find((certificate) => certificate.uid === userCertificateUid);
    if (!issued) {
      return ObservableResult.ofSuccess(undefined);
    }
    return ObservableResult.ofSuccess({
      uid: issued.uid,
      learnerName: 'Learner Name',
      title: issued.title,
      issuingOrganization: 'Potentially Ltd',
      completionDate: issued.completionDate,
      expiresOn: issued.expiresOn,
      expired: CertificateHelper.isExpired(issued.expiresOn),
    });
  }

  private loadStore(): Record<string, Certificate> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, Certificate>) : {};
    } catch {
      return {};
    }
  }

  private persistStore(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.store));
    } catch {
      // localStorage unavailable (e.g. private browsing) — state still survives for the current session.
    }
  }
}
